import React from "react";
import { fmtDays, fmtYears, fmtPct, fmtMoney } from "@/lib/chart-utils";

/**
 * A3 landscape poster designed to be captured at scale=3 for ~300 DPI.
 * Render dimensions: 1754 × 1240 px (≈ A3 landscape at 150 DPI native).
 * html2canvas @ scale=3 → ~5262 × 3720 px output (300 DPI effective).
 */

const POSTER_WIDTH = 1754;
const POSTER_HEIGHT = 1240;

function MiniCalendar({ daysForState = 0, totalDays = 365 }) {
  const rows = 19;
  const cols = 20; // 19*20 = 380 cells, we cap at 365
  const dots = [];
  for (let i = 0; i < rows * cols && i < totalDays; i += 1) {
    const isStateDay = i < Math.round(daysForState);
    dots.push(
      <rect
        key={i}
        x={(i % cols) * 22}
        y={Math.floor(i / cols) * 22}
        width={16}
        height={16}
        rx={3}
        fill={isStateDay ? "#C0392B" : "#1F7A3E"}
      />
    );
  }
  return (
    <svg
      width={cols * 22}
      height={rows * 22}
      viewBox={`0 0 ${cols * 22} ${rows * 22}`}
      aria-hidden="true"
    >
      {dots}
    </svg>
  );
}

export default function PosterTemplate({ project, analysis }) {
  const r = analysis?.results || {};
  const ind0 = r.profiles?.[0]?.individual || {};
  const debt = r.debt || {};
  const national = r.national || {};
  const currency = r.summary?.currency || "UM";
  const today = new Date().toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
  const daysForState = ind0.jours_vie_sacrifies_par_an || 0;

  return (
    <div
      id="vp-poster-template"
      data-testid="poster-template-root"
      style={{
        position: "absolute",
        left: "-10000px",
        top: 0,
        zIndex: -1,
        width: `${POSTER_WIDTH}px`,
        height: `${POSTER_HEIGHT}px`,
        backgroundColor: "#FDFBF4",
        color: "#0B1A2B",
        fontFamily: "Inter, system-ui, sans-serif",
        padding: "44px 56px",
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      {/* Header band */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "4px solid #C9A84C",
          paddingBottom: "18px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "46px",
              fontWeight: 900,
              color: "#1A3C5E",
              letterSpacing: "0.04em",
              lineHeight: 1,
            }}
          >
            VITAE-PUBLICA™
          </div>
          <div
            style={{
              fontSize: "22px",
              color: "#1A3C5E",
              marginTop: 6,
              fontWeight: 600,
            }}
          >
            Les Finances Publiques sont des Tranches de Vies
          </div>
        </div>
        <div style={{ textAlign: "right", fontSize: "16px", color: "#374151" }}>
          <div style={{ fontWeight: 700, color: "#1A3C5E" }}>
            {project?.country || "—"} · Exercice {project?.fiscal_year || "—"}
          </div>
          <div style={{ fontStyle: "italic" }}>{today}</div>
        </div>
      </div>

      {/* KPIs */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: "14px",
          marginTop: "28px",
        }}
      >
        {[
          {
            label: "Jours / an sacrifiés",
            value: fmtDays(daysForState),
            color: "#C0392B",
          },
          {
            label: "Mois de vie",
            value:
              ind0.mois_vie_sacrifies != null
                ? `${ind0.mois_vie_sacrifies} mois`
                : "—",
            color: "#D97706",
          },
          {
            label: "Années carrière",
            value: fmtYears(ind0.annees_vie_sur_carriere),
            color: "#C9A84C",
          },
          {
            label: "% vie fiscale",
            value: fmtPct(ind0.ratio_vie_fiscale_pct),
            color: "#1A3C5E",
          },
          {
            label: "Années dette",
            value: fmtYears(debt.annees_vie_hypothequees),
            color: "#111827",
          },
        ].map((k, i) => (
          <div
            key={i}
            style={{
              border: `2px solid ${k.color}`,
              borderRadius: "10px",
              padding: "16px 18px",
              backgroundColor: "#FFFFFF",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                textTransform: "uppercase",
                letterSpacing: "0.09em",
                color: "#6b7280",
              }}
            >
              {k.label}
            </div>
            <div
              style={{
                fontSize: "32px",
                fontWeight: 900,
                color: k.color,
                marginTop: 6,
              }}
            >
              {k.value}
            </div>
          </div>
        ))}
      </div>

      {/* Middle band — calendar + quote */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "40px",
          marginTop: "32px",
        }}
      >
        <div
          style={{
            border: "2px solid #C9A84C",
            borderRadius: "10px",
            padding: "20px",
            backgroundColor: "#FFFFFF",
          }}
        >
          <div
            style={{
              fontSize: "18px",
              fontWeight: 800,
              color: "#1A3C5E",
              marginBottom: "10px",
            }}
          >
            Calendrier de vie fiscale
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "18px",
            }}
          >
            <MiniCalendar
              daysForState={daysForState}
              totalDays={365}
            />
            <div style={{ fontSize: "13px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 16,
                    height: 16,
                    backgroundColor: "#C0392B",
                    borderRadius: 3,
                  }}
                />
                Jours consacrés à l'État
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 16,
                    height: 16,
                    backgroundColor: "#1F7A3E",
                    borderRadius: 3,
                  }}
                />
                Jours restitués au contribuable
              </div>
              <div
                style={{
                  marginTop: 12,
                  padding: "8px 10px",
                  border: "1px solid #C9A84C",
                  borderRadius: 6,
                  backgroundColor: "#FBF6E6",
                  fontWeight: 700,
                  color: "#1A3C5E",
                }}
              >
                Libération fiscale :{" "}
                {ind0.jour_liberation_fiscale || "(à calculer)"}
              </div>
            </div>
          </div>
        </div>

        <div
          style={{
            border: "2px solid #1A3C5E",
            borderRadius: "10px",
            padding: "20px",
            backgroundColor: "#1A3C5E",
            color: "#FBF6E6",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              fontFamily: "Merriweather, serif",
              fontSize: "22px",
              fontStyle: "italic",
              lineHeight: 1.45,
            }}
          >
            « L'unité de compte des finances publiques n'est pas la monnaie,
            mais la vie humaine. Chaque prélèvement est une tranche d'existence
            arrachée au contribuable. »
          </div>
          <div
            style={{
              marginTop: 14,
              fontSize: "14px",
              letterSpacing: "0.04em",
              color: "#C9A84C",
              textAlign: "right",
            }}
          >
            — Ahmed ELY Mustapha,
            <br />
            <span style={{ fontStyle: "italic" }}>
              La Nouvelle Théorie des Finances Publiques
            </span>
          </div>
        </div>
      </div>

      {/* Footer — budget summary + disclaimer */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "2fr 1fr",
          gap: "24px",
          marginTop: "24px",
        }}
      >
        <div
          style={{
            border: "1px solid #D9CDA2",
            borderRadius: "10px",
            padding: "16px 20px",
            backgroundColor: "#FFFFFF",
          }}
        >
          <div
            style={{
              fontSize: "16px",
              fontWeight: 800,
              color: "#1A3C5E",
              marginBottom: "8px",
            }}
          >
            Budget national converti
          </div>
          <div style={{ fontSize: "14px" }}>
            {national.available ? (
              <>
                Le budget national représente{" "}
                <strong>{fmtYears(national.annees_vies_budget)}</strong>{" "}
                d'existence humaine cumulées.
              </>
            ) : (
              <span style={{ color: "#6b7280" }}>
                Données nationales non disponibles.
              </span>
            )}
            <br />
            Dette par contribuable :{" "}
            <strong>{fmtMoney(debt.dette_par_contribuable, currency)}</strong>.
          </div>
        </div>
        <div
          style={{
            border: "2px solid #C0392B",
            borderRadius: "10px",
            padding: "14px 18px",
            backgroundColor: "#FDECEA",
            color: "#7A1F17",
            fontSize: "13px",
          }}
        >
          <strong>⚠ DOCUMENT PÉDAGOGIQUE</strong> — Conforme à la Nouvelle
          Théorie des Finances Publiques (ELY Mustapha). Aucune valeur
          juridictionnelle.
        </div>
      </div>

      {/* Attribution strip */}
      <div
        style={{
          marginTop: "14px",
          paddingTop: "10px",
          borderTop: "1px solid #D9CDA2",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontSize: "11px",
          color: "#6b7280",
        }}
      >
        <span>
          <strong style={{ color: "#1A3C5E" }}>
            Auteur de la méthodologie :
          </strong>{" "}
          Ahmed ELY Mustapha
        </span>
        <span style={{ fontStyle: "italic" }}>
          <strong style={{ fontStyle: "normal", color: "#1A3C5E" }}>
            Programmeur :
          </strong>{" "}
          ELY Mustapha — PMP · I-PMP · IBM Full Stack Developer
        </span>
      </div>
    </div>
  );
}
